import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { PrepAdminLayout } from '../../../components/prep/PrepAdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const blank = { program_id: '', title: '', type: 'video', content_url: '', order: 0, is_preview: false };

const TYPE_COLORS = { video: 'bg-blue-100 text-blue-700', pdf: 'bg-green-100 text-green-700', practice_test: 'bg-purple-100 text-purple-700', interactive: 'bg-orange-100 text-orange-700' };

export const PrepAdminMaterials = () => {
  const [materials, setMaterials] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [filterProgram, setFilterProgram] = useState('all');
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(blank);
  const [isCreating, setIsCreating] = useState(false);
  const [open, setOpen] = useState(false);

  const load = () => {
    Promise.all([
      axios.get(`${API}/prep/admin/materials`, { withCredentials: true }),
      axios.get(`${API}/prep/admin/programs`, { withCredentials: true })
        .catch(() => axios.get(`${API}/prep/programs`))
    ]).then(([mRes, pRes]) => {
      setMaterials(mRes.data);
      setPrograms(pRes.data);
    }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(blank); setIsCreating(true); setOpen(true); };
  const openEdit = (m) => { setForm({ ...m }); setIsCreating(false); setOpen(true); };

  const save = async () => {
    try {
      const data = { ...form, order: parseInt(form.order) || 0 };
      if (isCreating) {
        await axios.post(`${API}/prep/materials`, data, { withCredentials: true });
        toast.success('Material created');
      } else {
        await axios.put(`${API}/prep/materials/${form.id}`, data, { withCredentials: true });
        toast.success('Material updated');
      }
      setOpen(false);
      load();
    } catch { toast.error('Failed to save material'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this material?')) return;
    try {
      await axios.delete(`${API}/prep/materials/${id}`, { withCredentials: true });
      toast.success('Deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  const filtered = filterProgram === 'all' ? materials : materials.filter(m => m.program_id === filterProgram);

  return (
    <PrepAdminLayout title="Manage Materials">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Label>Filter by Program:</Label>
          <select value={filterProgram} onChange={e => setFilterProgram(e.target.value)} className="px-3 py-2 border rounded-md text-sm">
            <option value="all">All Programs</option>
            {programs.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
        </div>
        <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="add-material-btn">
          <Plus size={16} className="mr-2" /> Add Material
        </Button>
      </div>

      {loading ? <div className="text-center py-8 text-gray-500">Loading...</div> : (
        <div className="space-y-3">
          {filtered.map((m) => (
            <Card key={m.id} className="border">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-semibold text-gray-900">{m.title}</span>
                      <Badge className={TYPE_COLORS[m.type] || 'bg-gray-100 text-gray-700'}>{m.type.replace('_', ' ')}</Badge>
                      {m.is_preview && <Badge className="bg-yellow-100 text-yellow-700">Preview</Badge>}
                    </div>
                    <div className="text-xs text-gray-500">
                      Program: {m.program_title} &bull; Order: {m.order}
                    </div>
                    <div className="text-xs text-blue-600 truncate max-w-md mt-1">{m.content_url}</div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button size="sm" variant="outline" onClick={() => openEdit(m)} data-testid={`edit-material-${m.id}`}>
                      <Edit size={14} className="mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => del(m.id)} className="text-red-600" data-testid={`delete-material-${m.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {filtered.length === 0 && <p className="text-center text-gray-500 py-8">No materials found.</p>}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{isCreating ? 'Add Material' : 'Edit Material'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Program *</Label>
              <select value={form.program_id} onChange={e => setForm({ ...form, program_id: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm">
                <option value="">Select program</option>
                {programs.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </div>
            <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
            <div>
              <Label>Type *</Label>
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm">
                <option value="video">Video</option>
                <option value="pdf">PDF</option>
                <option value="practice_test">Practice Test</option>
                <option value="interactive">Interactive</option>
              </select>
            </div>
            <div><Label>Content URL *</Label><Input value={form.content_url} onChange={e => setForm({ ...form, content_url: e.target.value })} placeholder="https://..." /></div>
            <div><Label>Display Order</Label><Input type="number" value={form.order} onChange={e => setForm({ ...form, order: e.target.value })} /></div>
            <div className="flex items-center gap-2">
              <Label>Is Preview (visible without enrollment)</Label>
              <Switch checked={form.is_preview} onCheckedChange={v => setForm({ ...form, is_preview: v })} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-blue-600 hover:bg-blue-700">{isCreating ? 'Add' : 'Save'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </PrepAdminLayout>
  );
};
